import React, { useEffect, useState } from "react";

import Button from "@material-ui/core/Button";
import Checkbox from "@material-ui/core/Checkbox";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import FormControl from "@material-ui/core/FormControl";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import TextField from "@material-ui/core/TextField";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { toast } from "react-toastify";

import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import toastError from "../../errors/toastError";
import { getContactDisplayName } from "../../helpers/contactDisplayName";

const createRequestKey = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `visit-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
};

const relationship = (client) => {
  if (client?.customerType === "contract") return "Contrato registrado";
  if (client?.customerType === "occasional") return "Cliente ocasional";
  return "Por validar";
};

const GnsOsVisitRequestModal = ({ modalOpen, onClose, ticket }) => {
	const displayName = getContactDisplayName(ticket.contact);
  const [clients, setClients] = useState([]);
  const [links, setLinks] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [site, setSite] = useState("");
  const [reason, setReason] = useState("");
  const [details, setDetails] = useState("");
  const [priority, setPriority] = useState("normal");
  const [preferredDate, setPreferredDate] = useState("");
  const [preferredTimeWindow, setPreferredTimeWindow] = useState("");
  const [notifyContact, setNotifyContact] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [requestKey, setRequestKey] = useState(createRequestKey);

  useEffect(() => {
    if (!modalOpen || !ticket.id) return;
    const load = async () => {
      setLoadingData(true);
      try {
        const [clientsResponse, linksResponse] = await Promise.all([
          api.get("/tickets/gns-os/clients"),
          api.get(`/tickets/${ticket.id}/gns-os-visit-requests`),
        ]);
        setClients(clientsResponse.data.clients || []);
        setLinks(linksResponse.data.links || []);
		setReason(`Visita tecnica - ${displayName}`);
      } catch (err) {
        toastError(err);
      } finally {
        setLoadingData(false);
      }
    };
    load();
  }, [modalOpen, ticket.id, ticket.contact]);

  const handleClose = () => {
    setSelectedClient(null);
    setSite("");
    setReason("");
    setDetails("");
    setPriority("normal");
    setPreferredDate("");
    setPreferredTimeWindow("");
    setNotifyContact(true);
    setRequestKey(createRequestKey());
    onClose();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!selectedClient) return;
    setLoading(true);
    try {
      const { data } = await api.post(`/tickets/${ticket.id}/gns-os-visit-requests`, {
        gnsOsClientId: selectedClient.id,
        site,
        reason,
        details,
        priority,
        preferredDate,
        preferredTimeWindow,
        notifyContact,
        requestKey,
      });
      setLinks((current) => [data.link, ...current]);
      setSelectedClient(null);
      setSite("");
		setReason(`Visita tecnica - ${displayName}`);
      setDetails("");
      setPreferredDate("");
      setPreferredTimeWindow("");
      setRequestKey(createRequestKey());
      toast.success(`Solicitud de visita #${data.link.gnsOsVisitRequestId} creada`);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={modalOpen} onClose={handleClose} maxWidth="sm" fullWidth scroll="paper">
      <form onSubmit={handleSubmit}>
        <DialogTitle>Agendar visita tecnica en GNS-OS</DialogTitle>
        <DialogContent dividers>
          {links.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <strong>Solicitudes creadas en esta conversación</strong>
              {links.map((link) => (
                <TextField
                  key={link.id}
                  fullWidth
                  variant="outlined"
                  label="Solicitud de visita"
                  value={`#${link.gnsOsVisitRequestId}`}
                  InputProps={{ readOnly: true }}
                  style={{ marginTop: 10 }}
                />
              ))}
            </div>
          )}
          <Autocomplete
            options={clients}
            value={selectedClient}
            onChange={(_event, value) => setSelectedClient(value)}
            getOptionLabel={(option) => option.nombre || ""}
            renderOption={(option) => `${option.nombre} - ${relationship(option)}`}
            loading={loadingData}
            renderInput={(params) => (
              <TextField
                {...params}
                required
                label="Cliente de GNS-OS"
                variant="outlined"
                autoFocus
                InputProps={{
                  ...params.InputProps,
                  endAdornment: <>{loadingData ? <CircularProgress color="inherit" size={18} /> : null}{params.InputProps.endAdornment}</>,
                }}
              />
            )}
          />
          {selectedClient && (
            <TextField
              fullWidth
              variant="outlined"
              label="Relacion comercial registrada"
              value={`${relationship(selectedClient)}${selectedClient.horasContratadas ? ` - ${selectedClient.horasContratadas} hs` : ""}`}
              InputProps={{ readOnly: true }}
              style={{ marginTop: 12 }}
            />
          )}
          <TextField fullWidth variant="outlined" label="Sede o direccion" value={site} onChange={(event) => setSite(event.target.value)} inputProps={{ maxLength: 180 }} style={{ marginTop: 20 }} />
          <TextField fullWidth required variant="outlined" label="Motivo de la visita" value={reason} onChange={(event) => setReason(event.target.value)} inputProps={{ maxLength: 180 }} style={{ marginTop: 20 }} />
          <TextField fullWidth multiline rows={4} variant="outlined" label="Detalle para Operacion" value={details} onChange={(event) => setDetails(event.target.value)} inputProps={{ maxLength: 6000 }} style={{ marginTop: 20 }} />
          <FormControl fullWidth variant="outlined" style={{ marginTop: 20 }}>
            <InputLabel>Prioridad</InputLabel>
            <Select value={priority} onChange={(event) => setPriority(event.target.value)} label="Prioridad">
              <MenuItem value="baja">Baja</MenuItem><MenuItem value="normal">Normal</MenuItem><MenuItem value="alta">Alta</MenuItem><MenuItem value="urgente">Urgente</MenuItem>
            </Select>
          </FormControl>
          <TextField fullWidth type="date" variant="outlined" label="Fecha preferida" value={preferredDate} onChange={(event) => setPreferredDate(event.target.value)} InputLabelProps={{ shrink: true }} style={{ marginTop: 20 }} />
          <TextField fullWidth variant="outlined" label="Franja horaria preferida" value={preferredTimeWindow} onChange={(event) => setPreferredTimeWindow(event.target.value)} inputProps={{ maxLength: 120 }} placeholder="Ej. lunes a viernes, 09:00 a 12:00" style={{ marginTop: 20 }} />
          <FormControlLabel control={<Checkbox color="primary" checked={notifyContact} onChange={(event) => setNotifyContact(event.target.checked)} />} label="Avisar al cliente que registramos la solicitud" style={{ marginTop: 12 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="secondary" variant="outlined" disabled={loading}>Cancelar</Button>
          <ButtonWithSpinner variant="contained" type="submit" color="primary" loading={loading || loadingData}>Crear solicitud</ButtonWithSpinner>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default GnsOsVisitRequestModal;
